﻿import { customElement } from 'lit/decorators.js';
import { EventSnippet } from '../../../webmodule-common/other/ui/events.js';
import { EventTermsOpen } from '../data/events.js';
import { getApiFactory } from '../../api/api-injector.js';
import { getQuoteSupplierDisplayName } from '../../quotes/data/quoteSupplierProvider.js';
import { html } from 'lit';
import { isMultiSupplier } from '../../quotes/quote-service.js';
import { LangTermsConditionsListTable } from '../../../localization/scripts/franchisee/view/terms-conditions-list-table-lang.js';
import { DataTableWrapper } from '../../../webmodule-common/other/ui/datatable-view.js';
import { RequestPage, ResultPaginated } from '../../../webmodule-common/other/ui/RequestPage.js';
import {
  ResultBrowseTermsAndConditionsSummary,
  TermsAndConditionsSummary
} from '../../api/dealer-api-interface-franchisee.js';
import { TermsAndConditionsApi } from '../../api/terms-and-conditions-api.js';
import { userDataStore } from '../../dealer-franchisee/common/current-user-data-store.js';
import {
  WebModuleTable,
  WebModuleTableColumnDef
} from '../../../webmodule-common/components/src/webmodule-components.js';

export interface TermsConditionsSummaryTableOptions {
  title: EventSnippet;
  supplierId: string | null;
  openEvent: EventTermsOpen;
  addButtonTitle: EventSnippet;
  pageFragment: string;
  onSelected: (termsAndConditionsSummary: TermsAndConditionsSummary) => void;
}

@customElement('wm-termsconditionssummarytable')
export class TermsConditionsSummaryTable extends DataTableWrapper<TermsAndConditionsSummary> {
  eventTitle: EventSnippet;
  api: TermsAndConditionsApi = getApiFactory().termsAndConditions();
  filter: string | null;
  supplierId: string | null = null;
  openEvent: EventTermsOpen;
  addButtonTitle: EventSnippet;
  pageFragment: string;

  constructor(options: Omit<TermsConditionsSummaryTableOptions, 'onSelected'>) {
    super();
    this.eventTitle = options.title;
    this.filter = null;
    this.supplierId = options.supplierId;
    this.openEvent = options.openEvent;
    this.addButtonTitle = options.addButtonTitle;
    this.pageFragment = options.pageFragment;
  }

  // Full-page tab table: fill the panel and keep the paginator pinned to the
  // bottom instead of floating under the rows. The picker subclass overrides
  // this back to false (it renders content-height inside a modal).
  override fillHeight(): boolean {
    return true;
  }

  async getRowsFromServer(request: RequestPage): Promise<ResultPaginated<TermsAndConditionsSummary>> {
    const results = await this.api.browseTermsAndConditionsSummary({
      branchId: userDataStore.defaultBranch.id,
      sortField: request.sortField,
      sortAsc: !request.sortAsc,
      filter: this.filter,
      requestPage: {
        pageSize: request.pageSize,
        pageIndex: request.pageIndex
      },
      supplierId: this.supplierId
    });
    if (!results)
      return {
        count: 0,
        pageCount: 0,
        pageIndex: 0,
        pageSize: this.pageLength(),
        results: []
      };

    await Promise.all(this.getPreFetched(results));

    return results.termsAndConditionsSummary;
  }

  enableFiltering(): boolean {
    return true;
  }

  updateFilter(_searchTerm: string | null) {
    this.filter = _searchTerm;
  }

  getColumns(): WebModuleTableColumnDef[] {
    const columns: WebModuleTableColumnDef[] = [
      {
        title: () => LangTermsConditionsListTable.termsConditions(),
        fieldName: 'id',
        minWidth: '240px',
        sortable: true,
        fade: true,
        displayValue: (_table: WebModuleTable, item: unknown) => {
          const rowItem = item as TermsAndConditionsSummary;

          return html`<a class="terms-conditions-link" data-terms-conditions-id=${rowItem.id} href="#"
            >${rowItem.name}</a
          >`;
        },
        click: (e: Event, _table: WebModuleTable, item: unknown) => {
          e.preventDefault();
          e.stopImmediatePropagation();

          const rowItem = item as TermsAndConditionsSummary;

          this.eventHandler(async () => {
            await this.openTerms(rowItem);
          });

          return true;
        }
      },
      {
        title: () => LangTermsConditionsListTable.isDefault(),
        fieldName: 'value',
        width: '120px',
        align: 'center',
        displayValue: (_table: WebModuleTable, item: unknown) => {
          const rowItem = item as TermsAndConditionsSummary;
          return rowItem.isDefault
            ? html` <webmodule-icon library="fa" name="far-circle-check"></webmodule-icon> `
            : html``;
        }
      }
    ];

    if (isMultiSupplier())
      columns.push({
        title: () => LangTermsConditionsListTable.supplier(),
        fieldName: 'supplierId',
        minWidth: '160px',
        maxWidth: '300px',
        displayValue: (_table: WebModuleTable, item: unknown) => {
          const rowItem = item as TermsAndConditionsSummary;
          return html`${getQuoteSupplierDisplayName(rowItem.supplierId)}`;
        }
      });

    return columns;
  }

  // Uses the base single-row bodyTemplate (filter + table in one .col-12) so the
  // fill-height flex chain in dealer_ui.scss keeps the filter at its natural
  // height and lets the table absorb the rest. A split two-row layout would make
  // both rows grow equally and leave a large gap above the table.

  protected getPreFetched(_results: ResultBrowseTermsAndConditionsSummary): Promise<void>[] {
    return [];
  }

  protected async openTerms(termsAndConditionsSummary: TermsAndConditionsSummary): Promise<void> {
    await this.openEvent(termsAndConditionsSummary);
  }
}

@customElement('wm-termsconditionssummarypickerbrowser')
export class TermsConditionsSummaryPickerBrowser extends TermsConditionsSummaryTable {
  onSelected?: (termsAndConditionsSummary: TermsAndConditionsSummary) => void;

  constructor(options: Omit<TermsConditionsSummaryTableOptions, 'addButtonTitle' | 'openEvent' | 'pageFragment'>) {
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    super({ ...options, addButtonTitle: () => '', openEvent: async () => {}, pageFragment: '' });
    this.onSelected = options.onSelected;
    this.clickrows = true;
  }

  // Picker renders content-height inside a modal, not as a full-page tab, so
  // keep the base DataTableWrapper default rather than the page table's fill.
  override fillHeight(): boolean {
    return false;
  }

  getColumns(): WebModuleTableColumnDef[] {
    return [
      {
        title: () => LangTermsConditionsListTable.termsConditions(),
        fieldName: 'id',
        minWidth: '240px',
        sortable: true,
        fade: true,
        displayValue: (_table: WebModuleTable, item: unknown) => {
          const rowItem = item as TermsAndConditionsSummary;

          return html`<span class="terms-conditions-link" data-terms-conditions-id=${rowItem.id}
            >${rowItem.name}</span
          >`;
        }
      }
    ];
  }

  async rowClicked(e: CustomEvent<{ table: WebModuleTable; item: TermsAndConditionsSummary }>): Promise<void> {
    e.stopImmediatePropagation();
    e.preventDefault();

    this.eventHandler(async () => {
      if (this.onSelected) this.onSelected?.(e.detail.item);
    });
  }
}
